// ============================================================================
// Shared presentational primitives for the National Health Intelligence
// module. Kept dependency-free (no chart library) and styled with the
// same Tailwind `wantok` palette + Card surfaces used across the app, so
// every tab reads as one integrated intelligence platform.
// ============================================================================

// ---- formatters ------------------------------------------------------------

export function fmtNum(n, dp = 0) {
  if (n == null) return '—'
  return Number(n).toLocaleString(undefined, {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  })
}

export function fmtPct(n, dp = 1) {
  if (n == null) return '—'
  return `${Number(n).toFixed(dp)}%`
}

// ---- KPI tile --------------------------------------------------------------

const TILE_COLOURS = {
  slate: 'border-sand-200/60 bg-white',
  wantok: 'border-wantok-200 bg-wantok-50',
  emerald: 'border-emerald-200 bg-emerald-50',
  sky: 'border-sky-200 bg-sky-50',
  amber: 'border-amber-200 bg-amber-50',
  rose: 'border-rose-200 bg-rose-50',
  indigo: 'border-indigo-200 bg-indigo-50',
  violet: 'border-violet-200 bg-violet-50',
}

export function Tile({ label, value, sublabel, colour = 'slate' }) {
  return (
    <div className={`border rounded-2xl px-4 py-3 ${TILE_COLOURS[colour] || TILE_COLOURS.slate}`}>
      <div className="text-2xl font-display font-bold text-slate-900 tabular-nums leading-tight">
        {value ?? '—'}
      </div>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 mt-0.5">{label}</div>
      {sublabel && <div className="text-[11px] text-slate-400 mt-0.5">{sublabel}</div>}
    </div>
  )
}

// ---- horizontal bar list ---------------------------------------------------
// items: [{ label, count }]. Bars are scaled to the largest count so the
// distribution reads at a glance without a chart library.

const BAR_COLOURS = {
  wantok: 'bg-wantok-500',
  coral: 'bg-coral-500',
  emerald: 'bg-emerald-500',
  sky: 'bg-sky-500',
  amber: 'bg-amber-500',
  rose: 'bg-rose-500',
  indigo: 'bg-indigo-500',
  violet: 'bg-violet-500',
}

export function BarList({ items, colour = 'wantok', showPctOf = null, max = null, emptyLabel }) {
  const rows = items || []
  if (rows.length === 0) return <Empty label={emptyLabel} />
  const top = max || Math.max(...rows.map((r) => r.count || 0), 1)
  return (
    <ul className="space-y-2">
      {rows.map((r, i) => {
        const pct = top > 0 ? (r.count / top) * 100 : 0
        const share = showPctOf ? (r.count / showPctOf) * 100 : null
        return (
          <li key={r.key || r.label || i} className="text-sm">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-slate-700 truncate">{r.label}</span>
              <span className="font-semibold tabular-nums text-slate-900 shrink-0">
                {fmtNum(r.count)}
                {share != null && (
                  <span className="text-[11px] text-slate-500 ml-2">{fmtPct(share, 0)}</span>
                )}
              </span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-1">
              <div
                className={`h-full rounded-full ${BAR_COLOURS[colour] || BAR_COLOURS.wantok}`}
                style={{ width: `${Math.min(100, pct)}%` }}
              />
            </div>
          </li>
        )
      })}
    </ul>
  )
}

// ---- prevalence list (count + pct + k-floor suppression) -------------------
// items: [{ key, label, count, pct, suppressed }]

export function PrevalenceList({ items, denominatorLabel, colour = 'wantok', emptyLabel }) {
  const rows = items || []
  if (rows.length === 0) return <Empty label={emptyLabel} />
  const visible = rows.filter((r) => !r.suppressed && r.count != null)
  const top = Math.max(...visible.map((r) => r.count || 0), 1)
  return (
    <ul className="space-y-2.5">
      {rows.map((r) => {
        if (r.suppressed || r.count == null) {
          return (
            <li key={r.key || r.label} className="text-sm flex items-center justify-between gap-2">
              <span className="text-slate-500">{r.label}</span>
              <Suppressed />
            </li>
          )
        }
        const pct = top > 0 ? (r.count / top) * 100 : 0
        return (
          <li key={r.key || r.label} className="text-sm">
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-slate-700 truncate">{r.label}</span>
              <span className="font-semibold tabular-nums text-slate-900 shrink-0">
                {fmtNum(r.count)}
                {r.pct != null && (
                  <span className="text-[11px] text-slate-500 ml-2">{fmtPct(r.pct)}</span>
                )}
              </span>
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden mt-1">
              <div
                className={`h-full rounded-full ${BAR_COLOURS[colour] || BAR_COLOURS.wantok}`}
                style={{ width: `${Math.min(100, pct)}%` }}
              />
            </div>
          </li>
        )
      })}
      {denominatorLabel && (
        <li className="text-[11px] text-slate-400 italic pt-1">{denominatorLabel}</li>
      )}
    </ul>
  )
}

// ---- small bits ------------------------------------------------------------

export function Suppressed() {
  return (
    <span
      className="text-[10px] uppercase tracking-wide border rounded-full px-2 py-0.5 bg-slate-50 text-slate-500 border-slate-200"
      title="Suppressed: fewer than the k=5 privacy floor. Counts this small are withheld to protect individuals."
    >
      below privacy floor
    </span>
  )
}

export function Empty({ label = 'Nothing to show for this period yet.' }) {
  return <div className="text-xs text-slate-400 italic py-3">{label}</div>
}

export function SectionIntro({ children }) {
  return (
    <div className="rounded-xl bg-gradient-to-r from-wantok-50 to-coral-50 border border-wantok-100 p-3 text-sm text-slate-800">
      {children}
    </div>
  )
}

// ---- derivation / methodology note -----------------------------------------
// A compact "how this is derived" line shown under a tab's header so
// non-technical readers understand what the numbers mean.

export function MethodNote({ children }) {
  return (
    <div className="text-[11px] text-slate-500 leading-relaxed mb-3 -mt-1">
      <span className="font-semibold text-slate-600">How this is derived: </span>
      {children}
    </div>
  )
}

// ---- diagnosis rank table --------------------------------------------------

export function RankTable({ rows, columns }) {
  if (!rows || rows.length === 0) return <Empty />
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[11px] uppercase tracking-wide text-slate-500 border-b">
          <tr>
            <th className="text-left p-2 w-10">#</th>
            {columns.map((c) => (
              <th key={c.key} className={`p-2 ${c.align === 'right' ? 'text-right' : 'text-left'} ${c.width || ''}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-slate-50">
              <td className="p-2 text-slate-400 tabular-nums">{i + 1}</td>
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={`p-2 ${c.align === 'right' ? 'text-right tabular-nums font-semibold' : ''} ${c.cellClass || ''}`}
                >
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ---- multi-series monthly line chart (inline SVG, no deps) ------------------
// series: [{ key, label, colour }]; data: [{ month, [key]: number }]

const SERIES_STROKE = {
  wantok: '#0d9488',
  coral: '#ee4d1f',
  sky: '#0284c7',
  amber: '#d97706',
  violet: '#7c3aed',
}

export function LineChart({ data, series, height = 240 }) {
  const months = data || []
  if (months.length === 0) return <Empty />

  const W = 720
  const H = height
  const padL = 44
  const padR = 16
  const padT = 16
  const padB = 36
  const plotW = W - padL - padR
  const plotH = H - padT - padB

  const allVals = []
  for (const m of months) for (const s of series) allVals.push(Number(m[s.key] || 0))
  const maxV = Math.max(...allVals, 1)

  const n = months.length
  const x = (i) => padL + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW)
  const y = (v) => padT + plotH - (v / maxV) * plotH

  // 4 horizontal gridlines / y-axis ticks.
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((t) => Math.round(maxV * t))

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ minWidth: 520 }} role="img">
        {/* gridlines + y labels */}
        {ticks.map((tv, i) => {
          const gy = y(tv)
          return (
            <g key={i}>
              <line x1={padL} y1={gy} x2={W - padR} y2={gy} stroke="#e2e8f0" strokeWidth="1" />
              <text x={padL - 6} y={gy + 3} textAnchor="end" fontSize="10" fill="#94a3b8">
                {tv.toLocaleString()}
              </text>
            </g>
          )
        })}
        {/* x labels — show ~6 to avoid crowding */}
        {months.map((m, i) => {
          const every = Math.ceil(n / 6)
          if (i % every !== 0 && i !== n - 1) return null
          return (
            <text key={i} x={x(i)} y={H - 12} textAnchor="middle" fontSize="10" fill="#94a3b8">
              {m.month}
            </text>
          )
        })}
        {/* series lines + dots */}
        {series.map((s) => {
          const stroke = SERIES_STROKE[s.colour] || SERIES_STROKE.wantok
          const d = months
            .map((m, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(Number(m[s.key] || 0)).toFixed(1)}`)
            .join(' ')
          return (
            <g key={s.key}>
              <path d={d} fill="none" stroke={stroke} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
              {months.map((m, i) => (
                <circle key={i} cx={x(i)} cy={y(Number(m[s.key] || 0))} r="2.6" fill={stroke} />
              ))}
            </g>
          )
        })}
      </svg>
      {/* legend */}
      <div className="flex flex-wrap gap-4 mt-2 px-2">
        {series.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1.5 text-xs text-slate-600">
            <span
              className="inline-block w-3 h-3 rounded-full"
              style={{ background: SERIES_STROKE[s.colour] || SERIES_STROKE.wantok }}
            />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  )
}
